import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import * as L from 'leaflet';
import type { GeoJsonObject, MultiPolygon, Polygon } from 'geojson';
import { TileCoordinate, tilesForViewport, tileRectangle, viewportForTiles } from '../../core/map-tile-cache';
import { tileBoundsAtZoom } from '../../core/spatial/tile-math';
import { firstValueFrom, Subscription } from 'rxjs';
import { MapNavigationService } from '../../core/map-navigation.service';
import { AreaFeatureBase } from '../../core/base/area-feature.base';
import { MapFocusObject } from '../../core/map-object-focus';
import { MapObject } from '../../core/map-object';
import { PopoverFeature } from '../../core/popover-feature';
import { MapViewport, sameMapViewport } from '../../core/spatial/map-viewport';

export interface ZipCodeAreaMapObject extends MapObject<string> {
  readonly zipCode: string;
  readonly boundaryGeoJson: string;
}
export interface ZipCodeAreaDetails {
  readonly zipCode: string;
  readonly censusYear: number;
  readonly counties: readonly {
    readonly code: string;
    readonly name: string;
    readonly stateCode: string;
    readonly stateName: string;
    readonly stateAbbreviation: string;
  }[];
}
export interface ZipCodeAreaMapResult {
  readonly items: ZipCodeAreaMapObject[];
  readonly zoomInRequired: boolean;
}

/** Public ZIP boundaries with their own selection and no tenant Area records. */
@Injectable({ providedIn: 'root' })
export class ZipCodeAreaFeature extends AreaFeatureBase<ZipCodeAreaMapObject>
implements OnDestroy, PopoverFeature<ZipCodeAreaMapObject> {
  override readonly key = 'zipcode-areas';
  override readonly name = 'ZIP Codes';
  override readonly icon = 'local_post_office';
  override readonly iconColor = '#64748b';
  override objects: ZipCodeAreaMapObject[] = [];
  loading = false;
  readonly status = null;
  count: number | null = null;
  private viewport?: MapViewport;
  private loadedViewport?: MapViewport;
  private request?: Subscription;
  private requestKey?: string;
  private readonly tiles = new Map<string, readonly ZipCodeAreaMapObject[]>();
  private readonly tileCapacity = 200;
  private map?: L.Map;
  private popoversEnabled = false;
  private readonly onUnload = () => this.detach();

  private readonly selectionSubscription: Subscription;

  constructor(
    private readonly http: HttpClient,
    private readonly zone: NgZone,
    private readonly navigation: MapNavigationService,
  ) {
    super();
    this.selectionSubscription = this.selection.changed$.subscribe(ids => this.updateSelectedShapes(ids));
  }

  /** Resolve geometry without loading or enabling the ZIP viewport layer. */
  async navigateToZipCode(zipCode: string): Promise<void> {
    zipCode = zipCode.trim();
    if (!/^[0-9]{5}$/.test(zipCode)) throw new Error('Provide a five-digit US ZIP Code.');
    let target: MapFocusObject | undefined;
    await this.navigation.locate(firstValueFrom(this.http.get<ZipCodeAreaMapObject>(
      `/api/zipcode-areas/${zipCode}/map-object`,
    )).then(object => target = this.focusTarget(object)));
    if (target && this.navigation.hasObjectFocus(this.key, zipCode)) this.loadFocusDetails(target);
  }

  private focusTarget(object: ZipCodeAreaMapObject, anchor?: L.LatLng): MapFocusObject {
    const target: MapFocusObject = {
      reference: { featureKey: this.key, objectId: object.id },
      geometry: JSON.parse(object.boundaryGeoJson), anchor,
      popup: { type: 'ZIP Code', title: object.zipCode, subtitle: 'Loading location details...' },
    };
    return target;
  }

  private loadFocusDetails(target: MapFocusObject): void {
    this.http.get<ZipCodeAreaDetails>(`/api/zipcode-areas/${target.reference.objectId}`).subscribe({
      next: details => this.navigation.updateFocusPopup(target, {
        type: 'ZIP Code', title: details.zipCode, subtitle: `${details.censusYear} Census geography`,
        values: details.counties.length
          ? details.counties.map(county => ({ label: county.name, value: county.stateName }))
          : [{ label: 'Location', value: 'County details unavailable.' }],
      }),
      error: () => this.navigation.updateFocusPopup(target, {
        type: 'ZIP Code', title: String(target.reference.objectId), subtitle: 'Location details unavailable.',
      }),
    });
  }

  inspectObject(id: string | number): void {
    const object = this.objects.find(item => item.id === id);
    if (object) this.focusObject(object);
  }

  focusObject(object: ZipCodeAreaMapObject, anchor?: L.LatLng): void {
    const target = this.focusTarget(object, anchor);
    void this.navigation.focus(target);
    this.loadFocusDetails(target);
  }

  attach(map: L.Map): void {
    const viewport = this.viewport;
    if (this.map) this.detach();
    this.viewport = viewport;
    this.map = map;
    this.useSelectionMap(map);
    // A dedicated pane keeps ZIP boundaries below business shapes,
    // including after either feature is hidden and shown again.
    const pane = map.getPane('zipcode-areas') ?? map.createPane('zipcode-areas');
    pane.style.zIndex = '350';
    pane.style.pointerEvents = 'auto';
    map.on('unload', this.onUnload);
    this.render();
  }

  detach(): void {
    this.map?.off('unload', this.onUnload);
    this.map = undefined;
    this.clear();
    this.viewport = undefined;
    this.loadedViewport = undefined;
    this.objects = [];
    this.count = null;
  }

  onViewportChanged(viewport: MapViewport): void {
    if (sameMapViewport(this.viewport, viewport)) return;
    this.viewport = viewport;
    if (this.enabled) this.load();
  }

  override render(): void {
    if (!this.enabled) return;
    this.draw();
    if (this.viewport && !sameMapViewport(this.loadedViewport, this.viewport)
        && !this.loading) this.load();
  }

  override clear(): void {
    this.clearMapData();
  }

  private clearMapData(): void {
    this.request?.unsubscribe();
    this.request = undefined;
    this.requestKey = undefined;
    this.loading = false;
    this.shapesByObjectId.clear();
    super.clear();
  }

  reload(): void {
    this.tiles.clear();
    this.loadedViewport = undefined;
    if (this.enabled && this.viewport) this.load();
  }

  ngOnDestroy(): void {
    this.detach();
    this.selectionSubscription.unsubscribe();
  }

  enablePopovers(): void { this.popoversEnabled = true; }

  disablePopovers(): void { this.popoversEnabled = false; }

  private load(): void {
    const viewport = this.viewport;
    if (!viewport || !this.enabled) return;
    if (viewport.zoom < 11 || viewport.east - viewport.west >= 360) {
      this.clearMapData();
      this.objects = [];
      this.count = null;
      this.changed$.next();
      return;
    }

    // Customer uses one cache tile per display tile. ZIP polygons use tiles
    // two levels coarser (4x the width), with z8 as the minimum cache level.
    const zoom = Math.floor(viewport.zoom);
    const tileZoom = Math.max(8, zoom - 2);
    const visible = tilesForViewport({ ...viewport, zoom: tileZoom });
    const missing = visible.filter(tile => !this.tiles.has(this.tileKey(tile, zoom)));
    if (missing.length === 0) {
      this.clearMapData();
      this.showTiles(visible, viewport);
      return;
    }
    const requested = tileRectangle(missing);
    const requestKey = requested.map(tile => this.tileKey(tile, zoom)).join('|');
    if (this.loading && this.requestKey === requestKey) return;

    this.clearMapData();
    this.objects = this.snapshot(visible, zoom);
    this.count = this.objects.length || null;
    this.loadedViewport = undefined;
    this.draw();

    // As with Customer, the wire request is the rectangle covering missing
    // complete tiles. Display zoom remains independent of cache tile size.
    const bounds = viewportForTiles(requested);
    const longitude = (value: number) => ((value + 180) % 360 + 360) % 360 - 180;
    this.loading = true;
    this.requestKey = requestKey;
    this.changed$.next();
    this.request = this.http.get<ZipCodeAreaMapResult>('/api/zipcode-areas/map', {
      params: {
        west: longitude(bounds.west), east: longitude(bounds.east),
        south: bounds.south, north: bounds.north, zoom,
      },
    }).subscribe({
      next: result => {
        this.loading = false;
        this.requestKey = undefined;
        if (result.zoomInRequired) {
          // An incomplete response must never mark any tile as fully loaded.
          this.objects = [];
          this.count = null;
          this.loadedViewport = this.viewport;
          this.draw();
          this.changed$.next();
          return;
        }
        this.storeTiles(requested, zoom, result.items);
        const current = this.viewport ?? viewport;
        this.showTiles(tilesForViewport({ ...current, zoom: tileZoom }), current);
        // Read the entire visible result before evicting older tiles.
        while (this.tiles.size > this.tileCapacity) {
          this.tiles.delete(this.tiles.keys().next().value!);
        }
      },
      error: error => {
        this.loading = false;
        this.requestKey = undefined;
        console.error('Failed to load ZIP boundaries', error);
        this.changed$.next();
      },
    });
  }

  private tileKey(tile: TileCoordinate, displayZoom: number): string {
    return `${displayZoom}:${tile.zoom}/${tile.tileX}/${tile.tileY}`;
  }

  private snapshot(tiles: readonly TileCoordinate[], zoom: number): ZipCodeAreaMapObject[] {
    const objects = new Map<string, ZipCodeAreaMapObject>();
    for (const tile of tiles) {
      const key = this.tileKey(tile, zoom);
      const items = this.tiles.get(key);
      if (!items) continue;
      this.tiles.delete(key);
      this.tiles.set(key, items);
      for (const item of items) objects.set(item.id, item);
    }
    return Array.from(objects.values());
  }

  private showTiles(tiles: readonly TileCoordinate[], viewport: MapViewport): void {
    this.objects = this.snapshot(tiles, Math.floor(viewport.zoom));
    this.loadedViewport = viewport;
    this.count = this.objects.length;
    this.draw();
    this.changed$.next();
  }

  private storeTiles(
    tiles: readonly TileCoordinate[], zoom: number, items: readonly ZipCodeAreaMapObject[],
  ): void {
    const areas = items.map(item => {
      const geometry = JSON.parse(item.boundaryGeoJson) as Polygon | MultiPolygon;
      const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
      const bounds = L.latLngBounds([]);
      for (const polygon of polygons) {
        for (const ring of polygon) {
          for (const [longitude, latitude] of ring) bounds.extend([latitude, longitude]);
        }
      }
      return { item, bounds };
    });
    for (const tile of tiles) {
      const bounds = tileBoundsAtZoom(tile.tileX, tile.tileY, tile.zoom);
      const key = this.tileKey(tile, zoom);
      this.tiles.delete(key);
      // A boundary belongs to every intersecting tile, including when its
      // center is outside the requested rectangle. Bounding boxes conservatively
      // retain complete geometry; rendering and ZIP identity handle overlap.
      this.tiles.set(key, areas.filter(area => area.bounds.intersects(bounds)).map(area => area.item));
    }
  }

  private draw(): void {
    if (!this.map || !this.enabled) return;
    this.zone.runOutsideAngular(() => {
      this.layer.clearLayers();
      this.shapesByObjectId.clear();
      for (const object of this.objects) {
        const areaId = object.id;
        const selected = this.selection.has(areaId);
        // Spread consecutive ZIPs around the color wheel; identity alone keeps
        // the fill stable across tile loads, zoom changes and page refreshes.
        const hue = (Number(object.zipCode) * 137.508) % 360;
        const shape = L.geoJSON(JSON.parse(object.boundaryGeoJson) as GeoJsonObject, {
          pane: 'zipcode-areas',
          style: { color: this.iconColor, weight: 1.25, opacity: 0.8,
            fillColor: `hsl(${hue.toFixed(1)} 55% 48%)`, fillOpacity: 0.16,
            className: `tr-pickable${selected ? ' map-area-selected' : ''}` },
        });
        shape.on('click', event => {
          L.DomEvent.stop(event);
          this.zone.run(() => {
            const mouse = event as L.LeafletMouseEvent;
            if (this.selectionEnabled) {
              if (!this.selectObject(areaId, mouse.originalEvent) || !this.selection.has(areaId)) return;
            }
            if (this.popoversEnabled) this.focusObject(object, mouse.latlng);
          });
        });
        this.shapesByObjectId.set(areaId, shape);
        const label = document.createElement('span');
        label.textContent = object.zipCode;
        // Labels live with the shapes, below business layers, and never catch clicks.
        shape.bindTooltip(label, {
          permanent: true, direction: 'center', interactive: false,
          pane: 'zipcode-areas', className: 'zipcode-area-label',
        });
        this.layer.addLayer(shape);
      }
      this.updateSelectedShapes(this.selection.ids);
      // Read all label boxes before changing visibility to avoid alternating
      // DOM writes and forced layout. Geometry remains visible in dense areas.
      const labels = this.layer.getLayers()
        .map(layer => (layer as L.GeoJSON).getTooltip()?.getElement())
        .filter((element): element is HTMLElement => !!element)
        .map(element => ({ element, bounds: element.getBoundingClientRect() }));
      const accepted: DOMRect[] = [];
      for (const { element, bounds } of labels) {
        if (accepted.some(other =>
          bounds.left < other.right + 3 && bounds.right + 3 > other.left
          && bounds.top < other.bottom + 3 && bounds.bottom + 3 > other.top)) {
          element.style.visibility = 'hidden';
        } else {
          accepted.push(bounds);
        }
      }
    });
  }
}
